import { useRef, useState, type FormEvent } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Application } from '@regigov/contracts';
import { ApiError } from '../api/client';
import { applicationsApi } from '../api/participants';
import { useAuth } from '../auth/AuthContext';
import { ParticipantFields, toFormValues, validateDetails, type FieldErrors, type ParticipantFormValues } from '../components/ParticipantForm';
import { DefinitionList, ProblemAlert, StatusBadge, formatDateTime } from '../components/ui';
import { HelpPage } from '../components/HelpPage';

const EDITABLE = new Set(['draft', 'returned']);

export function ApplicantWorkbench() {
  return (
    <Routes>
      <Route index element={<ApplicantHome />} />
      <Route path="help" element={<HelpPage />} />
      <Route path="*" element={<Navigate to="/applicant" replace />} />
    </Routes>
  );
}

function ApplicantHome() {
  const { user } = useAuth();
  const mine = useQuery({
    queryKey: ['applications', 'mine'],
    queryFn: applicationsApi.mine,
    retry: false,
  });

  const notStarted = mine.error instanceof ApiError && mine.error.status === 404;

  return (
    <>
      <h1>Participant application</h1>
      <p>Signed in as {user?.displayName}.</p>
      {mine.isLoading && <p className="muted">Loading…</p>}
      {mine.error && !notStarted && <ProblemAlert error={mine.error} />}
      {notStarted && (
        <>
          <p className="notice">
            Apply to become a registered participant. CER will assess your application; you can track its status here and respond if it is
            returned for correction.
          </p>
          <ApplicationForm application={null} />
        </>
      )}
      {mine.data && <ApplicationView application={mine.data} />}
    </>
  );
}

function ApplicationView({ application }: { application: Application }) {
  const { refresh } = useAuth();
  const status = application.status;
  return (
    <>
      <section className="card">
        <div className="row">
          <h2>{application.participant.legalName}</h2>
          <StatusBadge status={status} />
        </div>
        <DefinitionList
          items={[
            ['Participant ID', <code key="id">{application.participant.id}</code>],
            ['ABN', application.participant.abn],
            ['Type', application.participant.participantType],
            ['Started', formatDateTime(application.createdAt)],
            ['Submitted', formatDateTime(application.submittedAt)],
            ['Decided', formatDateTime(application.decidedAt)],
          ]}
        />
        {status === 'submitted' && <p className="muted">Your application is in the CER queue awaiting assessment.</p>}
        {status === 'under_assessment' && <p className="muted">A CER officer ({application.assessorName}) is assessing your application.</p>}
        {status === 'returned' && <p className="notice">CER has returned your application for correction. See the notes below, update the details and resubmit.</p>}
        {status === 'approved' && (
          <p className="notice">
            Your application was approved. Your account is now the participant administrator for {application.participant.legalName}.{' '}
            <button type="button" className="linklike" onClick={() => void refresh()}>
              Go to the participant workbench
            </button>
          </p>
        )}
        {status === 'rejected' && <p className="alert">Your application was rejected: {application.decisionReason}</p>}
      </section>

      {application.notes.length > 0 && (
        <section className="card">
          <h2>Notes from CER</h2>
          <ul className="notes">
            {application.notes.map((n) => (
              <li key={n.id}>
                <span className="muted">
                  {formatDateTime(n.createdAt)} · {n.kind} · {n.authorName ?? 'CER'}
                </span>
                <p>{n.body}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {EDITABLE.has(status) && <ApplicationForm key={application.participant.version} application={application} />}
    </>
  );
}

function ApplicationForm({ application }: { application: Application | null }) {
  const qc = useQueryClient();
  const [values, setValues] = useState<ParticipantFormValues>(() => toFormValues(application?.participant));
  const [authorised, setAuthorised] = useState(application?.declarations.authorisedToApply === true);
  const [truthful, setTruthful] = useState(application?.declarations.informationTrue === true);
  const [evidence, setEvidence] = useState(application?.declarations.evidenceNotes ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saved, setSaved] = useState<string | null>(null);
  // A draft created by a save-and-submit that then failed at the submit step
  // must be edited and resubmitted, not created a second time.
  const created = useRef<Application | null>(null);

  const save = useMutation({
    mutationFn: async (thenSubmit: boolean) => {
      const { details, errors: fieldErrors } = validateDetails(values);
      setErrors(fieldErrors);
      if (!details) throw new Error('Fix the highlighted fields');
      const draft = {
        details,
        declarations: {
          ...(authorised && { authorisedToApply: true as const }),
          ...(truthful && { informationTrue: true as const }),
          ...(evidence && { evidenceNotes: evidence }),
        },
      };
      const existing = application ?? created.current;
      let app = existing ? await applicationsApi.update(existing.id, draft) : await applicationsApi.create(draft);
      created.current = app;
      if (thenSubmit) app = await applicationsApi.submit(app.id);
      return app;
    },
    onSuccess: (app, thenSubmit) => {
      qc.setQueryData(['applications', 'mine'], app);
      setSaved(thenSubmit ? 'Application submitted' : 'Draft saved');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(null);
    save.mutate(false);
  }

  return (
    <form className="form card" onSubmit={onSubmit} noValidate>
      <h2>{application ? 'Application details' : 'New application'}</h2>
      <ParticipantFields values={values} onChange={setValues} errors={errors} disabled={save.isPending} />

      <fieldset>
        <legend>Declarations</legend>
        <label className="check">
          <input type="checkbox" name="authorisedToApply" checked={authorised} onChange={(e) => setAuthorised(e.target.checked)} />I am
          authorised to apply on behalf of this organisation
        </label>
        <label className="check">
          <input type="checkbox" name="informationTrue" checked={truthful} onChange={(e) => setTruthful(e.target.checked)} />
          The information provided is true and correct
        </label>
        <label>
          Evidence notes (optional)
          <textarea name="evidenceNotes" value={evidence} onChange={(e) => setEvidence(e.target.value)} rows={3} maxLength={2000} />
        </label>
      </fieldset>

      <ProblemAlert error={save.error} />
      {saved && <p className="notice">{saved}</p>}
      <div className="row">
        <button type="submit" disabled={save.isPending}>
          Save draft
        </button>
        <button type="button" className="secondary" disabled={save.isPending} onClick={() => save.mutate(true)}>
          {application?.status === 'returned' ? 'Resubmit to CER' : 'Submit to CER'}
        </button>
      </div>
    </form>
  );
}
